// Pure builders for the Sales-rep Order Intake form (turbo_sales.md §11.4 / §11 item 5).
// Kept separate from the React surface so payload shape + validation are
// testable without mounting components.

import type {
  CreateIntakeBody,
  DealerCreateIntakeBody,
  OpsOrderIntakeSalePath,
  LegacyPosHandoffIntakeBody,
} from "@/lib/orderIntakeClient";
import { isLocationUuid } from "@/lib/locations/resolveSellerLocationUuid";
import { applyPlantPacketUrlsToPayload } from "@/lib/ops/plantPacket";
import {
  manufacturerSubmissionSuccessCopy,
  resolveManufacturerSubmissionRoute,
  type OrderManufacturerPartner,
} from "@/lib/sales-intake/manufacturerSubmissionRouting";

export type SalesRepCustomerContactDraft = {
  name: string;
  phone: string;
  email: string;
  address: string;
};

export type SalesRepIntakeDraft = {
  /** Empty string = submit to this org's review queue. */
  destinationManufacturerOrgId: string;
  salePath: OpsOrderIntakeSalePath | "";
  sellerLocationId: string;
  /** Optional; stamped as host-shaped `total` so Audience / aggregation get a dollar. */
  saleAmountUsd: string;
  customer: SalesRepCustomerContactDraft;
  notes: string;
  orderFormUrl: string;
  floorPlanUrl: string;
};

/** Cross-surface deal-thread provenance stamped into intake raw_payload (v1). */
export type SalesRepIntakeProvenance = {
  lead_id?: string | null;
  linked_opportunity_id?: string | null;
};

export type SalesRepIntakeBuilt =
  | { ok: true; mode: "own_org"; body: CreateIntakeBody; contactId: string }
  | { ok: true; mode: "dealer_create"; body: DealerCreateIntakeBody; contactId: string }
  | { ok: true; mode: "legacy_pos_handoff"; body: LegacyPosHandoffIntakeBody; contactId: string }
  | { ok: false; error: string };

const CUSTOMER_SALE_PATHS: ReadonlyArray<{ id: OpsOrderIntakeSalePath; label: string }> = [
  { id: "cash", label: "Cash sale" },
  { id: "finance", label: "Financed" },
  { id: "rto", label: "Rent-to-own" },
];

export { CUSTOMER_SALE_PATHS };

function trimmedContact(draft: SalesRepCustomerContactDraft) {
  return {
    name: draft.name.trim(),
    phone: draft.phone.trim(),
    email: draft.email.trim(),
    address: draft.address.trim(),
  };
}

function parseSaleAmountUsd(raw: string): number | null {
  const n = Number(raw.replace(/[$,]/g, "").trim());
  if (!Number.isFinite(n) || n <= 0) return null;
  return Math.round(n * 100) / 100;
}

function buildRawPayload(
  contact: ReturnType<typeof trimmedContact>,
  notes: string,
  contactId: string,
  provenance: SalesRepIntakeProvenance = {},
  saleAmountUsd: string = "",
  packetUrls: { orderFormUrl: string; floorPlanUrl: string } = { orderFormUrl: "", floorPlanUrl: "" },
): { ok: true; raw: Record<string, unknown> } | { ok: false; error: string } {
  const raw: Record<string, unknown> = {
    submission_channel: "sales_intake_rep_form",
    customer_contact: { ...contact, id: contactId },
  };
  const trimmedNotes = notes.trim();
  if (trimmedNotes) raw.notes = trimmedNotes;
  const leadId = (provenance.lead_id ?? "").trim();
  const linkedOppId = (provenance.linked_opportunity_id ?? "").trim();
  if (leadId) raw.lead_id = leadId;
  if (linkedOppId) raw.linked_opportunity_id = linkedOppId;
  const total = parseSaleAmountUsd(saleAmountUsd);
  if (total != null) raw.total = total;
  const urls = applyPlantPacketUrlsToPayload(raw, packetUrls);
  if (!urls.ok) return { ok: false, error: urls.error };
  return { ok: true, raw };
}

type ManufacturerBodyBuilt =
  | { ok: true; body: DealerCreateIntakeBody; contactId: string }
  | { ok: false; error: string };

function buildManufacturerBody(
  draft: SalesRepIntakeDraft,
  contactId: string,
  raw_payload: Record<string, unknown>,
  targetManufacturerOrgId: string,
): ManufacturerBodyBuilt {
  if (!draft.sellerLocationId.trim()) {
    return { ok: false, error: "Seller location is required when submitting to a manufacturer." };
  }
  if (!isLocationUuid(draft.sellerLocationId.trim())) {
    return {
      ok: false,
      error: "Seller location must be selected from your org locations list (not a legacy lot key).",
    };
  }
  return {
    ok: true,
    contactId,
    body: {
      target_manufacturer_org_id: targetManufacturerOrgId,
      proposed_seller_location_id: draft.sellerLocationId.trim(),
      proposed_sale_path: draft.salePath as OpsOrderIntakeSalePath,
      proposed_end_customer_contact_id: contactId,
      raw_payload,
    },
  };
}

export function buildSalesRepIntakeSubmit(
  draft: SalesRepIntakeDraft,
  contactId: string,
  partners: readonly OrderManufacturerPartner[] = [],
  provenance: SalesRepIntakeProvenance = {},
): SalesRepIntakeBuilt {
  const contact = trimmedContact(draft.customer);
  if (!contact.name) return { ok: false, error: "Customer name is required." };
  if (!contact.phone && !contact.email) {
    return { ok: false, error: "Enter at least a phone number or email for the customer." };
  }
  if (!draft.salePath) return { ok: false, error: "Sale path is required." };

  const builtRaw = buildRawPayload(
    contact,
    draft.notes,
    contactId,
    provenance,
    draft.saleAmountUsd,
    { orderFormUrl: draft.orderFormUrl, floorPlanUrl: draft.floorPlanUrl },
  );
  if (!builtRaw.ok) return { ok: false, error: builtRaw.error };
  const raw_payload = builtRaw.raw;
  const route = resolveManufacturerSubmissionRoute(draft.destinationManufacturerOrgId, partners);
  if (route.kind === "invalid") return { ok: false, error: route.error };

  if (route.kind === "own_org") {
    return {
      ok: true,
      mode: "own_org",
      contactId,
      body: {
        source: "in_app_customer",
        kind: "customer",
        proposed_sale_path: draft.salePath,
        proposed_end_customer_contact_id: contactId,
        proposed_seller_location_id: draft.sellerLocationId.trim() || null,
        proposed_options: {},
        raw_payload,
      },
    };
  }

  const manufacturerBody = buildManufacturerBody(
    draft,
    contactId,
    raw_payload,
    route.partner.id,
  );
  if (!manufacturerBody.ok) return manufacturerBody;

  if (route.kind === "dealer_create") {
    return {
      ok: true,
      mode: "dealer_create",
      contactId: manufacturerBody.contactId,
      body: manufacturerBody.body,
    };
  }

  return {
    ok: true,
    mode: "legacy_pos_handoff",
    contactId: manufacturerBody.contactId,
    body: manufacturerBody.body,
  };
}

export function salesRepIntakeSuccessMessage(
  built: Extract<SalesRepIntakeBuilt, { ok: true }>,
  partners: readonly OrderManufacturerPartner[],
): string {
  if (built.mode === "own_org") {
    return "Order submitted — now in your org's Pending Review queue.";
  }
  const route = resolveManufacturerSubmissionRoute(built.body.target_manufacturer_org_id, partners);
  const unified = manufacturerSubmissionSuccessCopy(route);
  if (unified) return unified;
  return "Order submitted.";
}

export function newSalesRepIntakeDraft(
  defaults: Partial<Pick<SalesRepIntakeDraft, "sellerLocationId" | "destinationManufacturerOrgId">> = {},
): SalesRepIntakeDraft {
  return {
    destinationManufacturerOrgId: defaults.destinationManufacturerOrgId ?? "",
    salePath: "",
    sellerLocationId: defaults.sellerLocationId ?? "",
    saleAmountUsd: "",
    customer: { name: "", phone: "", email: "", address: "" },
    notes: "",
    orderFormUrl: "",
    floorPlanUrl: "",
  };
}
